/*
 * Design: Command Center — Dark Operations Dashboard
 * 3-column grid with minimal cards — icon, title, description
 * Gradient border hover effect, zinc-900 surface cards
 */

import { useEffect, useRef } from "react";
import { Settings, Workflow, BarChart3, Globe, Zap, Bot } from "lucide-react";

const services = [
  {
    icon: Settings,
    title: "GHL Buildouts",
    description:
      "Complete GoHighLevel setups from scratch — CRM configuration, pipeline setup, custom fields, and full system architecture tailored to your agency.",
  },
  {
    icon: BarChart3,
    title: "GHL Audits & Optimization",
    description:
      "Already on GHL? We audit your existing setup and optimize workflows, automations, and funnels to eliminate bottlenecks and maximize performance.",
  },
  {
    icon: Workflow,
    title: "Workflows & Automations",
    description:
      "Advanced automation sequences, trigger-based workflows, and multi-step campaigns that run your agency operations on autopilot.",
  },
  {
    icon: Globe,
    title: "Funnels & Websites",
    description:
      "High-converting funnels and websites built inside GHL — designed for lead capture, client onboarding, and sales acceleration.",
  },
  {
    icon: Bot,
    title: "AI Systems Inside GHL",
    description:
      "Intelligent AI assistants embedded directly into your GHL environment — handling conversations, qualifying leads, and booking appointments.",
  },
  {
    icon: Zap,
    title: "Custom Integrations",
    description:
      "Connect GHL with your existing tech stack through custom API integrations, webhooks, and third-party platform bridges.",
  },
];

export default function ServicesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll("[data-card]");
            cards.forEach((card, i) => {
              setTimeout(() => {
                (card as HTMLElement).style.opacity = "1";
                (card as HTMLElement).style.transform = "translateY(0)";
              }, i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className="relative py-24 sm:py-32 noise-overlay">
      <div className="gradient-divider mb-24" />
      <div ref={sectionRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-2xl mb-16">
          <p className="text-sm font-semibold uppercase tracking-widest text-purple-400 mb-3">
            Services
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4">
            Everything GoHighLevel.
            <br />
            <span className="text-zinc-400">Done right.</span>
          </h2>
          <p className="text-zinc-300 text-lg leading-relaxed">
            From ground-up builds to surgical optimizations — we handle every layer of your GHL ecosystem so you can focus on closing deals.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <div
                key={service.title}
                data-card
                className="group relative rounded-xl bg-[#18181B] border border-white/5 p-6 sm:p-8 opacity-0 translate-y-5 transition-all duration-500 ease-out hover:border-purple-500/30 hover:bg-[#1C1C20]"
              >
                <div className="w-11 h-11 rounded-lg bg-white/5 flex items-center justify-center mb-5 group-hover:bg-purple-500/10 transition-colors duration-300">
                  <Icon size={22} className="text-zinc-400 group-hover:text-purple-400 transition-colors duration-300" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-3">{service.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{service.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
